import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { safeDownloadBlob } from "../lib/downloadUtils";
import { InnoHubRefBlock } from "../components/shared/InnoHubRefLink";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Plus, FileText, Calendar, Euro, Loader2, Bell, Trash2, Edit, AlertTriangle,
  CheckCircle, Clock, RefreshCw, Award, Shield, Globe, Building2, Download
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";

const PATENT_TYPES = [
  { value: "national", label: "Nacional (OEPM)" },
  { value: "european", label: "Europea (EPO)" },
  { value: "pct", label: "Internacional (PCT)" },
  { value: "utility_model", label: "Modelo de Utilidad" },
  { value: "trademark", label: "Marca" },
  { value: "design", label: "Diseño Industrial" },
];

const PATENT_STATUS = [
  { value: "draft", label: "Borrador" },
  { value: "filed", label: "Presentada" },
  { value: "examination", label: "En examen" },
  { value: "published", label: "Publicada" },
  { value: "granted", label: "Concedida" },
  { value: "expired", label: "Finalizada" },
  { value: "abandoned", label: "Abandonada" },
];

export default function PatentsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [patents, setPatents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState("patents");
  
  // Dialogs
  const [patentDialogOpen, setPatentDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingPatent, setEditingPatent] = useState(null);
  const [selectedPatent, setSelectedPatent] = useState(null);
  
  // Form states
  const [formLoading, setFormLoading] = useState(false);
  const [patentForm, setPatentForm] = useState({
    title: "", type: "national", status: "draft", application_number: "",
    filing_date: "", inventors: "", description: "", project_id: "",
    cost: "", payment_type: "single", payment_frequency: ""
  });
  const [paymentForm, setPaymentForm] = useState({
    concept: "", amount: "", due_date: "", notify_days_before: "30",
    is_recurring: false, recurrence_period: ""
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [patentsData, paymentsData] = await Promise.all([
        api.getPatents(token),
        api.getPatentPayments(token)
      ]);
      setPatents(patentsData || []);
      setPayments(paymentsData || []);
    } catch (error) {
      console.error("Error loading patents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePatent = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      // Clean up fields: empty strings → null for Optional numeric/date fields
      const payload = {
        ...patentForm,
        cost: patentForm.cost !== "" ? parseFloat(patentForm.cost) : null,
        filing_date: patentForm.filing_date || null,
        application_number: patentForm.application_number || null,
        inventors: patentForm.inventors || null,
        description: patentForm.description || null,
        project_id: patentForm.project_id || null,
        payment_frequency: patentForm.payment_frequency || null,
      };
      if (editingPatent) {
        await api.updatePatent(token, editingPatent.id, payload);
        toast.success("Patente actualizada");
      } else {
        await api.createPatent(token, payload);
        toast.success("Patente creada");
      }
      setPatentDialogOpen(false);
      resetPatentForm();
      loadData();
    } catch (error) {
      toast.error("Error al guardar patente");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeletePatent = async (patentId) => {
    if (!confirm("¿Eliminar esta patente y todos sus pagos?")) return;
    try {
      await api.deletePatent(token, patentId);
      toast.success("Patente eliminada");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!selectedPatent) return;
    setFormLoading(true);
    try {
      await api.createPatentPayment(token, {
        ...paymentForm,
        patent_id: selectedPatent.id,
        amount: parseFloat(paymentForm.amount),
        notify_days_before: parseInt(paymentForm.notify_days_before)
      });
      toast.success("Pago programado");
      setPaymentDialogOpen(false);
      resetPaymentForm();
      loadData();
    } catch (error) {
      toast.error("Error al crear pago");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!confirm("¿Eliminar este pago?")) return;
    try {
      await api.deletePatentPayment(token, paymentId);
      toast.success("Pago eliminado");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleMarkPaymentPaid = async (paymentId) => {
    try {
      await api.markPatentPaymentPaid(token, paymentId);
      toast.success("Pago marcado como realizado");
      loadData();
    } catch (error) {
      toast.error("Error");
    }
  };

  const openEditPatent = (patent) => {
    setEditingPatent(patent);
    setPatentForm({
      title: patent.title || "",
      type: patent.type || "national",
      status: patent.status || "draft",
      application_number: patent.application_number || "",
      filing_date: patent.filing_date || "",
      inventors: patent.inventors || "",
      description: patent.description || "",
      project_id: patent.project_id || "",
      cost: patent.cost || "",
      payment_type: patent.payment_type || "single",
      payment_frequency: patent.payment_frequency || ""
    });
    setPatentDialogOpen(true);
  };

  const openAddPayment = (patent) => {
    setSelectedPatent(patent);
    setPaymentDialogOpen(true);
  };

  const resetPatentForm = () => {
    setEditingPatent(null);
    setPatentForm({
      title: "", type: "national", status: "draft", application_number: "",
      filing_date: "", inventors: "", description: "", project_id: "",
      cost: "", payment_type: "single", payment_frequency: ""
    });
  };

  const resetPaymentForm = () => {
    setSelectedPatent(null);
    setPaymentForm({ 
      concept: "", amount: "", due_date: "", notify_days_before: "30",
      is_recurring: false, recurrence_period: ""
    });
  };

  const getPaymentStatus = (payment) => {
    if (payment.paid) return { color: "default", text: "Pagado", icon: CheckCircle };
    const days = differenceInDays(new Date(payment.due_date), new Date());
    if (days < 0) return { color: "destructive", text: "Vencido", icon: AlertTriangle };
    if (days <= 30) return { color: "warning", text: `${days} días`, icon: Clock };
    return { color: "secondary", text: format(new Date(payment.due_date), "dd/MM/yyyy"), icon: Calendar };
  };

  const getPatentPayments = (patentId) => {
    return payments.filter(p => p.patent_id === patentId);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const upcomingPayments = payments
    .filter(p => !p.paid && new Date(p.due_date) > new Date())
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 5);

  const overduePayments = payments.filter(p => !p.paid && new Date(p.due_date) < new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // KPI oficial "Patentes" (decisión equipo 2026-08-25): patente + MU + diseño industrial,
  // excluye marcas — no se borra la ficha de marca, sigue en la tabla completa de abajo.
  const kpiPatents = patents.filter(p => p.type !== "trademark");

  return (
    <div className="space-y-6 animate-in" data-testid="patents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Award className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Patentes y Propiedad Industrial</h1>
            <p className="text-muted-foreground">{kpiPatents.length} patentes (KPI oficial) · {patents.length} registros totales (incl. marcas) | {payments.filter(p => !p.paid).length} pagos pendientes</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={() => { resetPatentForm(); setPatentDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Patente
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="patents-total-kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Patentes</p>
                <p className="text-2xl font-bold">{kpiPatents.length}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Patente + MU + diseño industrial (excl. marcas)</p>
              </div>
              <Award className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Concedidas</p>
                <p className="text-2xl font-bold">{kpiPatents.filter(p => p.status === "granted").length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pagos Pendientes</p>
                <p className="text-2xl font-bold">{payments.filter(p => !p.paid).length}</p>
              </div>
              <Euro className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={overduePayments.length > 0 ? "border-red-200 dark:border-red-800" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pagos Vencidos</p>
                <p className={`text-2xl font-bold ${overduePayments.length > 0 ? "text-red-600" : ""}`}>
                  {overduePayments.length}
                </p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${overduePayments.length > 0 ? "text-red-500" : "text-muted-foreground"}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Payments Alert */}
      {upcomingPayments.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-600" />
              Próximos Pagos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {upcomingPayments.map(p => {
                const patent = patents.find(pat => pat.id === p.patent_id);
                return (
                  <Badge key={p.id} variant="outline" className="py-2">
                    <span className="font-medium">{patent?.title?.substring(0, 20)}...</span>
                    <span className="mx-2">|</span>
                    {formatCurrency(p.amount)}
                    <span className="mx-2">|</span>
                    {format(new Date(p.due_date), "dd/MM/yyyy")}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="patents"><Award className="w-4 h-4 mr-2" />Patentes</TabsTrigger>
          <TabsTrigger value="payments"><Euro className="w-4 h-4 mr-2" />Calendario de Pagos</TabsTrigger>
        </TabsList>

        {/* Patents Tab */}
        <TabsContent value="patents" className="space-y-4">
          {patents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Award className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-2">Sin patentes registradas</h3>
                <p className="text-muted-foreground mb-4">Registra tu primera patente o propiedad industrial</p>
                <Button onClick={() => { resetPatentForm(); setPatentDialogOpen(true); }}>
                  <Plus className="w-4 h-4 mr-2" />Nueva Patente
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {patents.map(patent => {
                const patentPayments = getPatentPayments(patent.id);
                const pendingPayments = patentPayments.filter(p => !p.paid);
                return (
                  <Card key={patent.id} className="card-hover">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{PATENT_TYPES.find(t => t.value === patent.type)?.label}</Badge>
                          <Badge variant={patent.status === "granted" ? "default" : "secondary"}>
                            {PATENT_STATUS.find(s => s.value === patent.status)?.label}
                          </Badge>
                        </div>
                        <div className="flex gap-1">
                          {patent.file_name && (
                            <Button
                              size="icon" variant="ghost"
                              onClick={() => safeDownloadBlob(`${process.env.REACT_APP_BACKEND_URL}/api/patents/${patent.id}/file`, patent.file_name, token)}
                              data-testid={`patent-download-${patent.id}`}
                              title={`Descargar ${patent.file_name}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" onClick={() => openEditPatent(patent)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDeletePatent(patent.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      <CardTitle className="text-lg mt-2">{patent.title}</CardTitle>
                      {patent.application_number && (
                        <CardDescription>Nº: {patent.application_number}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {patent.inventors && (
                        <p className="text-sm text-muted-foreground">Inventores: {patent.inventors}</p>
                      )}
                      {patent.filing_date && (
                        <p className="text-sm text-muted-foreground">
                          Fecha presentación: {format(new Date(patent.filing_date), "dd/MM/yyyy")}
                        </p>
                      )}
                      {patent.cost && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Coste:</span>{" "}
                          <span className="font-medium">{formatCurrency(patent.cost)}</span>
                          {patent.payment_type === "recurring" && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Recurrente {patent.payment_frequency === "annual" ? "(Anual)" : patent.payment_frequency === "quarterly" ? "(Trimestral)" : patent.payment_frequency === "monthly" ? "(Mensual)" : ""}
                            </Badge>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-sm text-muted-foreground">
                          {pendingPayments.length} pagos pendientes
                        </span>
                        <Button size="sm" variant="outline" onClick={() => openAddPayment(patent)}>
                          <Plus className="w-4 h-4 mr-1" />Añadir Pago
                        </Button>
                      </div>
                      {patent.refs?.length > 0 && (
                        <InnoHubRefBlock refs={patent.refs} />
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>Calendario de Pagos</CardTitle>
              <CardDescription>Gestión de tasas y anualidades</CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No hay pagos programados
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patente</TableHead>
                      <TableHead>Concepto</TableHead>
                      <TableHead>Importe</TableHead>
                      <TableHead>Fecha Vencimiento</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map(payment => {
                      const patent = patents.find(p => p.id === payment.patent_id);
                      const status = getPaymentStatus(payment);
                      return (
                        <TableRow key={payment.id}>
                          <TableCell className="font-medium">{patent?.title || "N/A"}</TableCell>
                          <TableCell>{payment.concept}</TableCell>
                          <TableCell>{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>{format(new Date(payment.due_date), "dd/MM/yyyy")}</TableCell>
                          <TableCell>
                            <Badge variant={status.color} className="gap-1">
                              <status.icon className="w-3 h-3" />
                              {status.text}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {!payment.paid && (
                                <Button size="sm" variant="ghost" onClick={() => handleMarkPaymentPaid(payment.id)}>
                                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => handleDeletePayment(payment.id)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Patent Dialog */}
      <Dialog open={patentDialogOpen} onOpenChange={setPatentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPatent ? "Editar Patente" : "Nueva Patente"}</DialogTitle>
            <DialogDescription>Información básica de la patente o propiedad industrial</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSavePatent} className="space-y-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input value={patentForm.title} onChange={(e) => setPatentForm({ ...patentForm, title: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={patentForm.type} onValueChange={(v) => setPatentForm({ ...patentForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PATENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={patentForm.status} onValueChange={(v) => setPatentForm({ ...patentForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PATENT_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nº Solicitud</Label>
                <Input value={patentForm.application_number} onChange={(e) => setPatentForm({ ...patentForm, application_number: e.target.value })} placeholder="P202X..." />
              </div>
              <div className="space-y-2">
                <Label>Fecha Presentación</Label>
                <Input type="date" value={patentForm.filing_date} onChange={(e) => setPatentForm({ ...patentForm, filing_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Inventores</Label>
              <Input value={patentForm.inventors} onChange={(e) => setPatentForm({ ...patentForm, inventors: e.target.value })} placeholder="Nombres separados por coma" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Coste (€)</Label>
                <Input type="number" step="0.01" value={patentForm.cost} onChange={(e) => setPatentForm({ ...patentForm, cost: e.target.value })} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Pago</Label>
                <Select value={patentForm.payment_type} onValueChange={(v) => setPatentForm({ ...patentForm, payment_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Único</SelectItem>
                    <SelectItem value="recurring">Recurrente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {patentForm.payment_type === "recurring" && (
              <div className="space-y-2">
                <Label>Frecuencia de Pago</Label>
                <Select value={patentForm.payment_frequency} onValueChange={(v) => setPatentForm({ ...patentForm, payment_frequency: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar frecuencia" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="quarterly">Trimestral</SelectItem>
                    <SelectItem value="annual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={patentForm.description} onChange={(e) => setPatentForm({ ...patentForm, description: e.target.value })} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPatentDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingPatent ? "Guardar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir Pago</DialogTitle>
            <DialogDescription>Patente: {selectedPatent?.title}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Concepto *</Label>
              <Input value={paymentForm.concept} onChange={(e) => setPaymentForm({ ...paymentForm, concept: e.target.value })} placeholder="Ej: Anualidad 3ª, Tasa de examen..." required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importe (€) *</Label>
                <Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Fecha Vencimiento *</Label>
                <Input type="date" value={paymentForm.due_date} onChange={(e) => setPaymentForm({ ...paymentForm, due_date: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Notificar días antes</Label>
                <Select value={paymentForm.notify_days_before} onValueChange={(v) => setPaymentForm({ ...paymentForm, notify_days_before: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 días</SelectItem>
                    <SelectItem value="14">14 días</SelectItem>
                    <SelectItem value="30">30 días</SelectItem>
                    <SelectItem value="60">60 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de Pago</Label>
                <Select value={paymentForm.is_recurring ? "recurring" : "single"} onValueChange={(v) => setPaymentForm({ ...paymentForm, is_recurring: v === "recurring" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Único</SelectItem>
                    <SelectItem value="recurring">Recurrente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {paymentForm.is_recurring && (
              <div className="space-y-2">
                <Label>Frecuencia</Label>
                <Select value={paymentForm.recurrence_period} onValueChange={(v) => setPaymentForm({ ...paymentForm, recurrence_period: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar frecuencia" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="quarterly">Trimestral</SelectItem>
                    <SelectItem value="annual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Bell className="w-3 h-3" />
              Recibirás alertas por email y Teams
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Añadir Pago
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
